// =============================================================================
// SimSense — Track Discovery Page (OT-236: Recent Tracks from user telemetry)
// =============================================================================

import { useEffect, useState, useMemo } from "react";
import { Link } from "react-router-dom";
import { useStore } from "../state/useStore";

interface TrackSummary {
  name: string;
  sessionCount: number;
  lastDriven: string;
  bestLapTime: number | null;
}

export function Tracks() {
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const { sessions, fetchSessions } = useStore();

  useEffect(() => {
    document.title = "Tracks | SimSense";
    fetchSessions().finally(() => setLoading(false));
  }, [fetchSessions]);

  // Build user's recent tracks from their sessions
  const recentTracks = useMemo<TrackSummary[]>(() => {
    const trackMap = new Map<string, { count: number; lastDate: string; bestLap: number | null }>();
    for (const s of sessions) {
      if (!s.trackName) continue;
      const existing = trackMap.get(s.trackName);
      if (!existing) {
        trackMap.set(s.trackName, { count: 1, lastDate: s.sessionDate, bestLap: s.bestLapTime ?? null });
      } else {
        existing.count++;
        if (s.sessionDate > existing.lastDate) existing.lastDate = s.sessionDate;
        if (s.bestLapTime && (!existing.bestLap || s.bestLapTime < existing.bestLap)) {
          existing.bestLap = s.bestLapTime;
        }
      }
    }
    return Array.from(trackMap.entries())
      .map(([name, data]) => ({
        name,
        sessionCount: data.count,
        lastDriven: data.lastDate,
        bestLapTime: data.bestLap,
      }))
      .sort((a, b) => b.lastDriven.localeCompare(a.lastDriven));
  }, [sessions]);

  const filteredRecent = useMemo(() => {
    if (!search) return recentTracks;
    return recentTracks.filter(t => t.name.toLowerCase().includes(search.toLowerCase()));
  }, [recentTracks, search]);

  if (loading) return <div style={{ padding: "40px", textAlign: "center" }}>Loading tracks...</div>;

  return (
    <div>
      <div style={{ marginBottom: "24px", display: "flex", justifyContent: "space-between", alignItems: "flex-end" }}>
        <div>
          <h2 style={{ fontSize: "28px", marginBottom: "8px" }}>Tracks</h2>
          <p style={{ color: "var(--text2)" }}>Your driven tracks from uploaded telemetry.</p>
        </div>
        <div style={{ width: "300px" }}>
          <input
            type="text"
            placeholder="Search tracks..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            style={{ width: "100%", padding: "10px 16px", background: "var(--card)", border: "1px solid var(--border)", color: "#fff", borderRadius: "8px" }}
          />
        </div>
      </div>

      {/* Your Tracks */}
      {filteredRecent.length > 0 && (
        <div>
          <h3 style={{ fontSize: "14px", color: "var(--text3)", textTransform: "uppercase", marginBottom: "16px", letterSpacing: "1px" }}>
            {search ? "Search Results" : "Your Tracks"}
          </h3>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(280px, 1fr))", gap: "16px" }}>
            {filteredRecent.map(track => (
              <Link
                key={track.name}
                to={`/leaderboard?track=${encodeURIComponent(track.name)}`}
                style={{
                  background: "var(--card)",
                  padding: "20px",
                  borderRadius: "12px",
                  border: "1px solid var(--border)",
                  textDecoration: "none",
                  color: "inherit",
                  transition: "all 0.2s ease",
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.borderColor = "var(--accent)";
                  e.currentTarget.style.transform = "translateY(-2px)";
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.borderColor = "var(--border)";
                  e.currentTarget.style.transform = "translateY(0)";
                }}
              >
                <h3 style={{ fontSize: "17px", marginBottom: "12px" }}>{track.name}</h3>
                <div style={{ display: "flex", gap: "16px", fontSize: "12px", color: "var(--text2)" }}>
                  <span>{track.sessionCount} session{track.sessionCount !== 1 ? "s" : ""}</span>
                  <span>{formatRelativeDate(track.lastDriven)}</span>
                  {track.bestLapTime && track.bestLapTime > 0 && (
                    <span style={{ color: "var(--good)", fontVariantNumeric: "tabular-nums" }}>
                      {formatLapTime(track.bestLapTime)}
                    </span>
                  )}
                </div>
              </Link>
            ))}
          </div>
        </div>
      )}

      {filteredRecent.length === 0 && (
        <div style={{ padding: "80px", textAlign: "center", background: "var(--card)", borderRadius: "12px", border: "1px solid var(--border)" }}>
          <div style={{ fontSize: "32px", marginBottom: "16px" }}>🏁</div>
          <div style={{ color: "var(--text2)" }}>
            {search ? `No tracks found matching "${search}"` : "No tracks yet. Upload a session to see your tracks here."}
          </div>
        </div>
      )}
    </div>
  );
}

function formatRelativeDate(dateStr: string): string {
  const d = new Date(dateStr);
  const now = new Date();
  const diffMs = now.getTime() - d.getTime();
  const days = Math.floor(diffMs / (1000 * 60 * 60 * 24));
  if (days === 0) return "Today";
  if (days === 1) return "Yesterday";
  if (days < 7) return `${days}d ago`;
  if (days < 30) return `${Math.floor(days / 7)}w ago`;
  return d.toLocaleDateString("en-US", { month: "short", day: "numeric" });
}

function formatLapTime(seconds: number): string {
  const mins = Math.floor(seconds / 60);
  const secs = (seconds % 60).toFixed(3);
  return mins > 0 ? `${mins}:${secs.padStart(6, "0")}` : `${secs}s`;
}
